import { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Plus, Trash2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function CaptionsSystem() {
  const [captions, setCaptions] = useState([]);
  const [aiPrompt, setAiPrompt] = useState({ niche: "", subject: "", platform: "instagram" });
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const load = async () => setCaptions(await api.listCaptions());
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!aiPrompt.subject) return toast.error("Précise un sujet");
    setLoading(true); setAiResult(null);
    try {
      const r = await api.aiCaption({ prompt: aiPrompt.subject, niche: aiPrompt.niche, platform: aiPrompt.platform });
      const m = (r.raw || "").match(/\{[\s\S]*\}/);
      if (m) setAiResult(JSON.parse(m[0]));
      else toast.error("Format IA invalide");
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!aiResult) return;
    await api.createCaption({
      title: aiPrompt.subject.slice(0, 60),
      text: aiResult.caption || "",
      hashtags: aiResult.hashtags || [],
      cta: aiResult.cta || "",
      platform: aiPrompt.platform,
    });
    toast.success("Caption enregistrée");
    setAiResult(null);
    load();
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };
  const del = async (id) => { await api.deleteCaption(id); load(); };

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="captions-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// CRÉATION & IA</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Système de Captions</h1>
        <p className="text-zinc-400 mt-2">Génère des captions virales avec hook + valeur + CTA + hashtags. Sauvegarde tes meilleures.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="border border-[#00D4FF]/30 bg-[#00D4FF]/[0.02] rounded-sm p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.5} />
            <h2 className="font-display text-xl font-semibold text-white">Génération IA</h2>
          </div>
          <input value={aiPrompt.niche} onChange={e => setAiPrompt({ ...aiPrompt, niche: e.target.value })}
            placeholder="Niche" data-testid="caption-niche"
            className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#00D4FF] rounded-sm px-3 py-2 text-sm text-white outline-none" />
          <textarea value={aiPrompt.subject} onChange={e => setAiPrompt({ ...aiPrompt, subject: e.target.value })}
            placeholder="Sujet du post / contexte" rows={4} data-testid="caption-subject"
            className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#00D4FF] rounded-sm px-3 py-2 text-sm text-white outline-none resize-none" />
          <select value={aiPrompt.platform} onChange={e => setAiPrompt({ ...aiPrompt, platform: e.target.value })}
            className="w-full bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="instagram">Instagram</option><option value="tiktok">TikTok</option>
            <option value="threads">Threads</option><option value="x">X / Twitter</option>
          </select>
          <button onClick={generate} disabled={loading} data-testid="generate-caption-btn"
            className="w-full bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-4 py-2.5 rounded-sm font-mono uppercase tracking-wider text-sm flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Génération..." : "Générer la caption"}
          </button>

          {aiResult && (
            <div className="border border-[#1E1E24] bg-[#0C0C0E] rounded-sm p-4 space-y-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Caption</div>
                <p className="text-sm text-white whitespace-pre-line">{aiResult.caption}</p>
              </div>
              {aiResult.cta && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">CTA</div>
                  <p className="text-sm text-[#CCFF00]">{aiResult.cta}</p>
                </div>
              )}
              {aiResult.hashtags?.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Hashtags</div>
                  <div className="text-xs font-mono text-zinc-400">{aiResult.hashtags.map(h => `#${h.replace(/^#/,"")}`).join(" ")}</div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={save} className="bg-[#CCFF00] text-black px-4 py-1.5 rounded-sm text-sm font-medium" data-testid="save-caption">
                  Sauver
                </button>
                <button onClick={() => copy(aiResult.caption)} className="border border-[#1E1E24] text-zinc-400 hover:text-white px-4 py-1.5 rounded-sm text-sm flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Captions sauvegardées · {captions.length}</div>
          {captions.length === 0 ? (
            <div className="border border-dashed border-[#1E1E24] rounded-sm p-8 text-center text-zinc-500 text-sm">
              Aucune caption pour le moment.
            </div>
          ) : captions.map(c => (
            <div key={c.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4" data-testid={`caption-row-${c.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm text-white font-medium truncate flex-1">{c.title}</div>
                <button onClick={() => del(c.id)} className="text-zinc-600 hover:text-red-500 ml-2"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-zinc-400 line-clamp-3 whitespace-pre-line">{c.text}</p>
              <div className="flex items-center gap-2 mt-2 text-[10px] font-mono uppercase tracking-wider text-[#CCFF00]">
                {c.platform} · {c.hashtags?.length || 0} tags
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
